namespace Core.Hotels {

    export interface HotelRoom {
        Title : string;
        Availability : RoomAvailability[];
    }

    export interface HotelSummary {
        Id: number;
        CustomerRating: number;
        CustomerRatingTitle: string;
        CustomerRatingCount: number;
        Location: string;
        StarRating: number;
        Title: string;
        Url: string;
    }

    export interface HotelWithRooms extends StaticHotel {

        OriginalCostPerPersonPerNightStr: string;
        OriginalCostPerNightStr: string;
        OriginalCostStr: string;
        MinCostPerPersonPerNightStr: string;
        MinCostPerNightStr: string;
        MinCostStr: string;
        OriginalCostPerPersonPerNight: number;
        OriginalCostPerPerson: number;
        OriginalCostPerNight: number;
        MinCostPerPersonPerNight: number;
        MinCostPerPerson: number;
        MinCostPerNight: number;
        LowDepositValue: number;
        IsPayAtHotelOnly: boolean;
        Rooms: HotelRoom[];
        EstabGroupIds: number[];

        Deposit: number;
        MinCost: number;
        NonRefundable: boolean;
        OriginalCost: number;
        PayAtHotelAvailable: boolean;
        RefundableCutOffDate: Date;
        TopSlotRank: number;

        HotelAward: HotelAward|undefined;
    }

    export interface StaticHotel extends HotelSummary {
        AcomTypeId: number;
        AcomType: string;
        Address: string;
        CallToBookBehaviour: number;
        Characteristics: string[];
        Country: string;
        CountryId: number;
        CurrentViews: number;
        DealMinPrice: number;
        Description: string;
        FacilityGroups: FacilityGroup[];
        HasReviewSnippets: boolean;
        ImageUrl: string;
        IndicativeMinPrice: number;
        IsVilla: boolean;
        Latitude: number;
        LocationId: number;
        Longitude: number;
        LongSpecialOfferText: string;
        NumberOfPhotos: number;
        ObjectTypeId: number;
        Postcode: string;
        Province: string;
        ProvinceId: number;
        Recommended: boolean;
        ReviewSnippets: ReviewSnippet[];
        RoomDetails: RoomDetails[];
        RoomNightsBooked: number;
        SpecialOfferText: string;
        StarRatingTitle: string;
        Summary: string;
        TeaserStr: string;
        earliestRefundableCutOffMsg: string;
        //the properties below are added in support of CMS hotels
        HotelName: string|undefined;
        CmsImageUrls: string[]|null|undefined;
        HideFacilitiesList: boolean|undefined;
        HideRoomFacilitiesList: boolean|undefined;
        IsPlatinumHotel: boolean|undefined;
        IsBoutiqueHotel: boolean|undefined;
        IsDsfHotel: boolean|undefined;
    }

    export enum GroupType {
        Facilities = 1,
        HolidayType = 2,
    }

    export enum BoardType {
        Mixed = 0,
        RoomOnly = 1,
        BedAndBreakfast = 2,
        HalfBoard = 3,
        FullBoard = 4,
        AllInclusive = 5,
        SelfCatering = 6,
    }

    export interface Money {
        Value: number;
        CurrencyCode: string;
    }

    export interface YieldManagementSupplierSwap {
        OrigSupplierId: number;
        OrigSupplierName: string;
        OrigSupplierNetCost: number;
        OrigSupplierClientCost: number;
        OrigSupplierDescription: string;
        NewSupplierId: number;
        NewSupplierName: string;
        NewSupplierNetCost: number;
        NewSupplierClientCost: number;
        NewSupplierDescription: string;
        NewSupplierCostChanged: boolean;
        OrigSupplierIncentive: number;
        NewSupplierIncentive: number;
        OrigSupplierDiscount: SupplierDiscount;
    }

    export interface SupplierDiscount {
        SupplierDiscountAmount: number;
        SupplierDiscountMessage: string;
        SupplierDiscountType: SupplierDiscountType;
    }

    export enum SupplierDiscountType {
        None = 1,
        Mobile = 2,
        Promotional = 3,
    }

    export interface ReviewSnippet {
        EstabId: number;
        ReviewId: number;
        Comments: string;
        Rating: number;
        RatingTitle: string;
        ReviewerName: string;
        ReviewerFrom: string;
        CultureCode: string;
        Translated: boolean;
        IsDefaultLanguage: boolean;
    }

    export interface FacilityGroup {
        Type: GroupType;
        Name: string;
        Codes: string[];
    }

    export interface HotelAward {
        ResourceString: string;
        Type: AwardType;
        Year: number;
    }

    export enum AwardType {
        Winner = 1,
        BestIn = 2,
    }

    export interface RoomDetails {
        MasterId: number;
        Title: string;
        Description: string;
        ImageUrl: string;
    }

    export interface ApiRequestedRoom {
        BoardTypeMinPrices: BoardTypeMinPriceFull[];
        Availability: ApiRoomAvailability[];
    }

    export interface BoardTypeMinPriceFull {
        BoardType: BoardType;
        MinCost: number;
        NetCost: number;
        OriginalCost: number;
        SpecialOfferText: string;
        LongSpecialOfferText: string;
        AllowDiscounts: boolean;
        MasterRoomId: number;
        RoomDescription: string;
        UserDiscount: boolean;
    }

    export interface ApiRoomAvailability {
        MasterId: number;
        RoomDescription: string;
        QuantityAvailable: number;
        Facilities: string[];
        BoardOptions: BoardOption[];
    }

    export type RoomAvailability = ApiRoomAvailability & BoardOption;

    export interface BoardOption {
        Id: string;
        BoardType: BoardType;
        BoardDescription: string;
        Cost: number;
        NetCost: number;
        OriginalCost: number;
        TotalLocalNetCost: Money;
        TotalLocalCost: Money;
        CityTax: number;
        IsPayAtHotel: boolean;
        IsOnRequest: boolean;
        IsShortlisted: boolean;
        LowDepositValue: number;
        NonRefundable: boolean;
        AllowDiscounts: boolean;
        SpecialOfferText: string;
        LongSpecialOfferText: string;
        CostCalculationDebugInfo: string;
        SupplierId: number;
        SupplierName: string;
        YieldManagementSupplierSwap: YieldManagementSupplierSwap;
        UserDiscount: boolean;
        CostPerNight: number;
        OriginalCostPerNight: number;
        IsCheapest: boolean;
        RefundableCutOffDate: string;
        BTInclusions: BoardTypeInclusion[];
        BTExclusions: BoardTypeInclusion[];
        InclusionText: string;
        ExclusionText: string;
    }

    export interface BoardTypeInclusion {
        InclusionId: number;
        ReplacementText: string;
    }

    /* when we rehydrate the basket we squash together the hotel and the details */
    export type HotelDetails = Details & HotelWithRooms;

    /* Hotel details interfaces */
    export interface DetailsRequest {
        AvailabilitySearchId: string;
        EstabId: number;
        DiscountCode: string;
        RoomAndBoardIds: string[];
        BookingPath: BookingPath;
    }

    export interface Details {
        DetailsId: string;
        Itinerary: Itinerary;
        BookedItems: Room[];
        AdditionalInformation: AdditionalInformationItem[];
        PromotionalInformation: InformationItem[];
        SpecialOfferText: string[];
        FreeTransfer: FreeTransfer;
        LongSpecialOfferText: string[];
    }

    export enum BookingPath {
        HotelOnly = 1,
        Holiday = 2,
        HotelDeal = 3,
        HolidayDeal = 4,
        MetaHotelOnly = 5,
        MetaHoliday = 6,
    }

    export interface Itinerary {
        EstabId: number;
        Country: string;
        Province: string;
        Location: string;
        Title: string;
        CheckInDate: string;
        CheckOutDate: string;
        Duration: number;
        StarRating: number;
    }

    export interface FreeTransfer {
        TransferType: string;
        Quantity: number;
        SupplierName: string;
        SupplierId: number;
    }

    export interface Room {
        Title: string;
        RoomNumber: number;
        NonRefundable: boolean;
        BoardDescription: string;
        RoomDescription: string;
        Price: number;
        IsOnRequest: boolean;
    }

    export interface InformationItem {
        Title: string;
        Description: string;
    }

    export interface AdditionalInformationItem extends InformationItem {
        ShowOnVoucher: boolean;
    }

    export enum BoostReason {
        UserClickPrimary = 1,
        UserClickSecondary = 2,
        UserClickTertiary = 3,
        UserClickOther = 4,
        TopSlot = 5,
        Recommended = 6,
    }
}
